import { Zap, TrendingUp } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { motion } from 'framer-motion';

const EmailAdPlatformSection = () => {
  const isMobile = useIsMobile();
  
  return (
    <section className="section relative py-24 overflow-hidden">
      {/* Background gradient effect */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-emerald-900/10 to-transparent" />
      
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="max-w-6xl mx-auto px-6 relative"
      >
        <div className="flex flex-col space-y-8">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            {/* Feature badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-emerald-500/10 to-teal-500/10 backdrop-blur-sm border border-emerald-500/20">
              <TrendingUp className="w-4 h-4 text-emerald-400" />
              <span className="text-sm font-medium bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">
                Conversion Tracking
              </span>
            </div>
            
            {/* Icon with glow effect */}
            <motion.div 
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              transition={{ duration: 0.5, type: "spring" }}
              viewport={{ once: true }}
              className="relative inline-block"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-teal-500 blur-xl opacity-50" />
              <div className="relative bg-gradient-to-br from-emerald-500/20 to-teal-500/20 backdrop-blur-sm border border-emerald-500/30 p-4 rounded-2xl">
                <Zap size={isMobile ? 28 : 32} className="text-white" />
              </div>
            </motion.div>
            
            <h3 className="text-3xl md:text-4xl font-bold">
              <span className="text-white">Email As An</span>{' '}
              <span className="bg-gradient-to-r from-emerald-400 via-teal-400 to-emerald-400 bg-clip-text text-transparent animate-gradient-x">
                Ad Platform
              </span>
            </h3>
            
            <p className="text-xl text-gray-300 leading-relaxed max-w-3xl">
              Install the Munger pixel on your website to{' '}
              <span className="text-white font-medium">track sales, appointments, and more</span>.{' '}
              Munger uses conversion data to improve messaging and targeting on its own, just like paid ad platforms.
            </p>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="relative"
          >
            {/* Glow effect */}
            <div className="absolute -inset-4 bg-gradient-to-r from-emerald-500/20 to-teal-500/20 blur-3xl opacity-60" />
            
            {/* Glass morphism container */}
            <div className="relative backdrop-blur-sm bg-gradient-to-br from-gray-800/40 to-gray-900/40 rounded-2xl border border-gray-700/50 p-1 shadow-2xl hover:shadow-emerald-500/20 transition-all duration-300">
              <div className="bg-gradient-to-br from-gray-900/80 to-gray-950/80 rounded-xl p-8 flex justify-center items-center">
                <div className="absolute inset-0 bg-gradient-to-tr from-emerald-500/5 to-teal-500/5 rounded-xl" />
                <svg viewBox="0 0 300 300" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto max-w-md relative z-10">
                  <defs>
                    <linearGradient id="magicalPixelGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style={{stopColor:"#10b981"}}>
                        <animate attributeName="stop-color" values="#10b981;#14b8a6;#10b981" dur="4s" repeatCount="indefinite"/>
                      </stop>
                      <stop offset="100%" style={{stopColor:"#14b8a6"}}>
                        <animate attributeName="stop-color" values="#14b8a6;#0d9488;#14b8a6" dur="4s" repeatCount="indefinite"/>
                      </stop>
                    </linearGradient>
                    
                    <radialGradient id="glowGrad" cx="50%" cy="50%" r="50%">
                      <stop offset="0%" style={{stopColor:"#10b981", stopOpacity:0.8}}/>
                      <stop offset="100%" style={{stopColor:"#14b8a6", stopOpacity:0.2}}/>
                    </radialGradient>
                    
                    <filter id="glow">
                      <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                      <feMerge>
                        <feMergeNode in="coloredBlur"/>
                        <feMergeNode in="SourceGraphic"/>
                      </feMerge>
                    </filter>
                  </defs>
                  
                  {/* Central pixel with glow */}
                  <rect x="130" y="130" width="40" height="40" fill="url(#magicalPixelGrad)" rx="4" filter="url(#glow)">
                    <animate attributeName="opacity" values="0.8;1;0.8" dur="2s" repeatCount="indefinite"/>
                  </rect>
                  
                  {/* Pixel icon */}
                  <path d="M140 140 Q150 135 160 140 Q165 150 160 160 Q150 165 140 160 Q135 150 140 140" 
                        fill="white" opacity="0.9">
                    <animate attributeName="opacity" values="0.9;1;0.9" dur="2s" repeatCount="indefinite"/>
                  </path>
                  
                  {/* Data flow indicators */}
                  <g fill="#10b981">
                    <polygon points="120,110 125,100 130,110" filter="url(#glow)">
                      <animate attributeName="opacity" values="0;1;0" dur="1.5s" repeatCount="indefinite" begin="0s"/>
                    </polygon>
                    
                    <polygon points="145,100 150,90 155,100" filter="url(#glow)">
                      <animate attributeName="opacity" values="0;1;0" dur="1.5s" repeatCount="indefinite" begin="0.3s"/>
                    </polygon>
                    
                    <polygon points="170,110 175,100 180,110" filter="url(#glow)">
                      <animate attributeName="opacity" values="0;1;0" dur="1.5s" repeatCount="indefinite" begin="0.6s"/>
                    </polygon>
                  </g>
                  
                  {/* Version indicators */}
                  <g opacity="0.8">
                    <rect x="80" y="180" width="20" height="20" fill="#6b7280" rx="2"/>
                    <text x="90" y="210" textAnchor="middle" fontSize="10" fill="#9ca3af">v1.0</text>
                    
                    <rect x="140" y="180" width="20" height="20" fill="#10b981" rx="2" filter="url(#glow)">
                      <animate attributeName="fill" values="#10b981;#14b8a6;#10b981" dur="4s" repeatCount="indefinite"/>
                    </rect>
                    <text x="150" y="210" textAnchor="middle" fontSize="10" fill="#9ca3af">v2.0</text>
                    
                    <rect x="200" y="180" width="20" height="20" fill="#14b8a6" rx="2" filter="url(#glow)">
                      <animate attributeName="opacity" values="0.8;1;0.8" dur="2s" repeatCount="indefinite"/>
                    </rect>
                    <text x="210" y="210" textAnchor="middle" fontSize="10" fill="#9ca3af">v3.0</text>
                  </g>
                  
                  {/* Rotating orbit */}
                  <circle cx="150" cy="150" r="80" fill="none" stroke="#10b981" strokeWidth="1" strokeDasharray="5,5" opacity="0.3">
                    <animateTransform attributeName="transform" type="rotate" values="0 150 150;360 150 150" dur="8s" repeatCount="indefinite"/>
                  </circle>
                  
                  {/* Progress indicator */}
                  <g transform="translate(220, 50)">
                    <circle r="25" fill="none" stroke="#374151" strokeWidth="2" opacity="0.3"/>
                    <circle r="25" fill="none" stroke="url(#magicalPixelGrad)" strokeWidth="2" 
                            strokeDasharray="157" strokeDashoffset="157" filter="url(#glow)">
                      <animate attributeName="strokeDashoffset" values="157;39;157" dur="4s" repeatCount="indefinite"/>
                    </circle>
                    <text x="0" y="5" textAnchor="middle" fontSize="10" fill="#10b981">75%</text>
                  </g>
                </svg>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

export default EmailAdPlatformSection; 